/**
 * Krok MOB1 — SBERAC KONTEXTU pro „Nahlásit problém".
 *
 * Prevzato z RedHand Mobil (`report-problem/collector.ts`, v ostrem provozu
 * pro SportActions a Pivo a chleba) a uprateno na konvence REOS (strict TS,
 * zadne `any`). Drzi v pameti prohlizece poslednich 20 chyb konzole a
 * neuspesnych volani API, aby hlaseni neslo jako „nejde to" bez stopy.
 *
 * BEZPECNOST (zadani: „bez obsahu tel a bez tokenu"): zaznamenava se jen
 * metoda, ADRESA BEZ DOTAZU a navratovy kod. Telo pozadavku ani odpovedi se
 * necte, hlavicky (a tedy `authorization` ani cookies) se necetou vubec a
 * dotaz v URL se odrezava — v nem jezdi jednorazove odkazy k podpisu.
 *
 * KROK CER1 bod 1 — TYZ SBER ZAPISUJE CHYBU I K NAM DO DB. Do CER1 zustaly
 * zachycene chyby jen v pameti zalozky a odesel je az makler tlacitkem
 * „Nahlásit problém" — bez hlaseni jsme o vade nevedeli (makleřka MV reality:
 * „u smlouvy něco nešlo", server 200). Odposlech se proto NEZDVOJUJE: tytez
 * posluchace navic volaji `reportClientError` (`client-error-reporter.ts`),
 * ktery udalost dava do fronty pro `POST /v1/client-errors`.
 *
 * ODESILA SE JEN PO PRIHLASENI: sber zapina `installReportCollector`,
 * odesilani teprve `startClientErrorReporting` (vola ho shell administrace po
 * nacteni session). Bez session se nic neposila.
 */
import {
  CLIENT_ERROR_ENDPOINT,
  disableClientErrorReporting,
  enableClientErrorReporting,
  reportClientError,
} from './client-error-reporter.js';
import { apiPathOf } from './client-error-scrub.js';
import { kodChyby } from './form-errors.js';
import { API_BASE_URL } from './api.js';

const MAX_ENTRIES = 20;
const MAX_LENGTH = 300;

/** Mutujici metody — jejich selhani znamena NEULOZENOU ZMENU (kind `unsaved`). */
const MUTATING_METHODS: readonly string[] = ['POST', 'PATCH', 'PUT', 'DELETE'];
/** Po teto dobe se nedokoncena mutace hlasi jako neulozena zmena (bod 1). */
const UNSAVED_TIMEOUT_MS = 15_000;
/** Kody, ktere se NEhlasi: 401 = vyprsela session, 404 = bezny „neni tu". */
const IGNORED_API_STATUSES: readonly number[] = [401, 404];

/**
 * Krok DROB1 (nalez #18 cileneho QA 22. 10.) — OCEKAVANY STAV NENI CHYBA.
 *
 * `client_error_events` v kancelari KR1 nesly za 24 h 51 zapisu 409
 * z akceptacnich toku: uklid po behu maze koncept, ktery uz videl klient
 * (server ma pravdu, ze ho smazat nelze, a uklid ho proto ARCHIVUJE), a
 * objednavka PDF se opakuje, dokud report nejde sestavit. Oboji je stav,
 * se kterym volajici POCITA a ktery umi obslouzit — ne vada. Sum z nich
 * pritom zakryval skutecne nalezy.
 *
 * VYJIMKA JE NA KOD, NE NA CESTU (CLAUDE.md §8 pravidlo 3): vyjimka na
 * `DELETE /v1/contracts/:id` by platila i pro jine 409 te routy, ktere uz
 * vada jsou. Stav 409 s JINYM kodem (nebo bez kodu) se hlasi dal.
 */
const EXPECTED_CONFLICT_CODES: readonly string[] = [
  // Koncept uz videla treti strana — uklid i obrazovka misto mazani archivuji.
  'documents/contract-has-activity',
  // Koncept uz neni koncept (odeslany/podepsany) — tataz obsluha.
  'documents/contract-not-draft',
  // Report se zatim nesestavuje (dobihaji mapy, nemovitost neni urcena);
  // obrazovka na to ma vetu „co doplnit" (PIL3 bod 4).
  'dd-exports/report-model-missing',
];

function isExpectedConflict(status: number | null, code: string | undefined): boolean {
  return status === 409 && code !== undefined && EXPECTED_CONFLICT_CODES.includes(code);
}

/**
 * Nase originy: adresa administrace a adresa API (v dev a v nativnim obalu to
 * neni totez). Dlazdice map ani RUIAN mezi ne nepatri — cizi vypadek neni nase
 * chyba a nema u nas co zapisovat.
 */
function ownOrigins(): readonly string[] {
  if (typeof window === 'undefined') return [];
  return [API_BASE_URL, window.location.origin].filter((origin) => origin !== '');
}

function isOwnApi(url: string): boolean {
  return url.startsWith('/') || ownOrigins().some((origin) => url.startsWith(origin));
}

/**
 * Strojovy kod chyby z `problem+json` (`type`, napr. `contract/not-ready`).
 * Cte se KLON odpovedi, aby volajici prisel o nic, a bere se JEN tenhle jeden
 * identifikator — nikdy `title`, `detail` ani cokoli jineho z tela (bod 5).
 *
 * KROK DROB1 (nalez #18): `type` chodi ze serveru jako PLNA URI
 * (`https://docs.reos.local/errors/documents/contract-has-activity`, tak ji
 * sklada `ProblemError`). Vzor nize dvojtecku nepripousti, takze se do
 * DROB1 zahodil KAZDY kod — `client_error_events.error_code` byl v preview
 * prazdny u vsech 409 (kontrola 22. 10. 2026) a podle kodu nesla udalost ani
 * zaradit, ani odfiltrovat. Prevod na holy kod dela `kodChyby` z
 * `form-errors.ts`, tedy TATAZ funkce, kterou uz na to pouzivaji formulare
 * a `office-migration-api.ts` — ne druha kopie.
 */
async function machineErrorCode(response: Response): Promise<string | undefined> {
  const contentType = response.headers.get('content-type') ?? '';
  if (!contentType.includes('json')) return undefined;
  try {
    const body = (await response.clone().json()) as { type?: unknown; code?: unknown };
    const raw = typeof body.type === 'string' ? body.type : typeof body.code === 'string' ? body.code : '';
    const code = kodChyby(raw);
    return /^[A-Za-z0-9._/-]{1,80}$/.test(code) ? code : undefined;
  } catch {
    return undefined;
  }
}

/** Zaznam neuspesneho volani vlastniho API do nasi DB (bod 1). */
function reportApiFailure(
  method: string,
  url: string,
  status: number | null,
  detail: string,
  code: string | undefined,
): void {
  if (!isOwnApi(url)) return;
  if (status !== null && IGNORED_API_STATUSES.includes(status)) return;
  if (isExpectedConflict(status, code)) return;
  // Origin se odrezava podle toho, ktery z nasich adresu volani patri — jinak
  // by v `api_path` zustala cela adresa API z jineho originu.
  const origin = ownOrigins().find((candidate) => url.startsWith(candidate)) ?? '';
  reportClientError({
    // Selhana mutace = ztracena zmena makleře, proto vlastni druh.
    kind: MUTATING_METHODS.includes(method) ? 'unsaved' : 'api',
    message: detail,
    apiMethod: method,
    apiPath: apiPathOf(url, origin),
    ...(status === null ? {} : { apiStatus: status }),
    ...(code === undefined ? {} : { errorCode: code }),
  });
}

const consoleErrors: string[] = [];
const failedRequests: string[] = [];

function remember(buffer: string[], entry: string): void {
  buffer.push(entry.slice(0, MAX_LENGTH));
  if (buffer.length > MAX_ENTRIES) buffer.shift();
}

function describe(value: unknown): string {
  return value instanceof Error ? value.message : String(value);
}

/** Adresa BEZ dotazu a fragmentu — v nich jezdi jednorazove tokeny. */
function safeUrl(input: RequestInfo | URL): string {
  const raw =
    typeof input === 'string'
      ? input
      : input instanceof URL
        ? input.href
        : input instanceof Request
          ? input.url
          : String(input);
  try {
    const url = new URL(raw, window.location.origin);
    return `${url.origin}${url.pathname}`;
  } catch {
    return raw.split('?')[0] ?? raw;
  }
}

let installed = false;
let originalConsoleError: typeof console.error | null = null;
let originalFetch: typeof window.fetch | null = null;
let onError: ((event: ErrorEvent) => void) | null = null;
let onRejection: ((event: PromiseRejectionEvent) => void) | null = null;

/** Zapne sber. Idempotentni. */
export function installReportCollector(): void {
  if (installed || typeof window === 'undefined') return;
  installed = true;

  originalConsoleError = console.error.bind(console);
  console.error = (...args: unknown[]): void => {
    remember(consoleErrors, args.map(describe).join(' '));
    originalConsoleError?.(...args);
  };

  onError = (event) => {
    remember(consoleErrors, `${event.message} (${event.filename}:${String(event.lineno)})`);
    reportClientError({
      kind: 'js',
      message: event.message,
      stack: event.error instanceof Error ? event.error.stack : `${event.filename}:${String(event.lineno)}:${String(event.colno)}`,
    });
  };
  onRejection = (event) => {
    remember(consoleErrors, `neodchycena chyba: ${describe(event.reason)}`);
    reportClientError({
      kind: 'promise',
      message: describe(event.reason),
      ...(event.reason instanceof Error ? { stack: event.reason.stack } : {}),
    });
  };
  window.addEventListener('error', onError);
  window.addEventListener('unhandledrejection', onRejection);

  originalFetch = window.fetch.bind(window);
  window.fetch = async (input: RequestInfo | URL, init?: RequestInit): Promise<Response> => {
    const url = safeUrl(input);
    const method = (
      init?.method ?? (input instanceof Request ? input.method : 'GET')
    ).toUpperCase();
    // Pojistka 1 proti smycce: vlastni hlaseni se neodposlouchava.
    if (url.endsWith(CLIENT_ERROR_ENDPOINT)) {
      return (originalFetch as typeof window.fetch)(input, init);
    }
    /*
     * Hlidac NEULOZENE ZMENY (bod 1): autosave, ktery se nedokoncil do 15 s,
     * makler nepozna — formular jen „jakoby uklada". Server o tom nevi vubec,
     * protoze pozadavek nedojel (odpojena sit, uspany telefon, proxy).
     */
    const unsavedWatch = MUTATING_METHODS.includes(method)
      ? setTimeout(() => {
          reportApiFailure(method, url, null, 'pozadavek se nedokoncil do 15 s', undefined);
        }, UNSAVED_TIMEOUT_MS)
      : undefined;
    try {
      const response = await (originalFetch as typeof window.fetch)(input, init);
      if (!response.ok) {
        remember(failedRequests, `${method} ${url} → ${String(response.status)}`);
        void machineErrorCode(response).then((code) => {
          reportApiFailure(method, url, response.status, `API ${String(response.status)}`, code);
        });
      }
      return response;
    } catch (error) {
      remember(failedRequests, `${method} ${url} → ${describe(error)}`);
      reportApiFailure(method, url, null, describe(error), undefined);
      throw error;
    } finally {
      if (unsavedWatch !== undefined) clearTimeout(unsavedWatch);
    }
  };
}

/**
 * Krok CER1 bod 1 — ZAPNE SBER I ODESILANI. Vola shell administrace, jakmile
 * je session prihlasena; idempotentni.
 *
 * Prenos dostane PUVODNI `fetch` (pojistka 1 proti smycce) — odeslane hlaseni
 * tak neprojde vlastnim odposlechem.
 */
export function startClientErrorReporting(): void {
  installReportCollector();
  const send = originalFetch;
  enableClientErrorReporting({
    transport:
      send === null
        ? undefined
        : (url: string, requestInit: RequestInit) => send(url, requestInit) as Promise<unknown>,
  });
}

/** Odhlaseni: odesilani se vypina, fronta se zahazuje (bez session nic neposilame). */
export function stopClientErrorReporting(): void {
  disableClientErrorReporting();
}

export function recentConsoleErrors(): readonly string[] {
  return [...consoleErrors];
}

export function recentFailedRequests(): readonly string[] {
  return [...failedRequests];
}

/** Jen pro testy: vrati `console.error`, `fetch` i posluchace do puvodniho stavu. */
export function resetReportCollectorForTests(): void {
  consoleErrors.length = 0;
  failedRequests.length = 0;
  disableClientErrorReporting();
  if (installed) {
    if (originalConsoleError !== null) console.error = originalConsoleError;
    if (typeof window !== 'undefined') {
      if (originalFetch !== null) window.fetch = originalFetch;
      if (onError !== null) window.removeEventListener('error', onError);
      if (onRejection !== null) window.removeEventListener('unhandledrejection', onRejection);
    }
  }
  installed = false;
  originalConsoleError = null;
  originalFetch = null;
  onError = null;
  onRejection = null;
}
